import { useEffect, useRef } from "react";
import styled from "styled-components";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/Table";
import { MembersChangeIndicator } from "../summary/members-change-indicator";
import type { ChangeRate } from "types/BusinessDashboard";

const StyledCard = styled(Card)`
  box-shadow: none;
  padding: 0;
  gap: 0;
  border: none;
  padding-left: 16px;
`;

const StyledCardHeader = styled(CardHeader)`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 0;
`;

const StyledCardTitle = styled(CardTitle)`
  font-size: 1rem;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.2px;
`;

const StyledCardContent = styled(CardContent)`
  padding: 0;
`;

const StyledTable = styled(Table)`
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  padding-right: 16px;
`;

const StyledTableHead = styled(TableHead)`
  width: 95px;
  min-width: 95px;
  max-width: 95px;
  height: 26px;
  border-right: 1px solid #e3e5e8;
  text-align: center;
  color: #4b4f58;
  font-size: 12px;
  font-weight: 600;
  background: #f4f5f6;
  position: sticky;
  left: 0;
  z-index: 3;
`;

const DataTableCell = styled(TableCell)`
  width: 95px;
  min-width: 95px;
  max-width: 95px;
  height: 46px;
  border-right: 1px solid #e3e5e8;
  border-bottom: 1px solid #e3e5e8;
  background: #fff;
  color: #4b4f58;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 2;
  padding: 0;
`;

const MonthTableHead = styled(TableHead)<{ $last?: boolean }>`
  width: 72px;
  min-width: 72px;
  max-width: 72px;
  height: 26px;
  text-align: center;
  color: #6c727f;
  font-size: 12px;
  font-weight: 500;
  border-right: 1px solid #e3e5e8;
  background: #f4f5f6;
`;

const StyledTableRow = styled(TableRow)`
  background: #ffffff;
  border-top: 1px solid #e3e5e8;
`;

const DataTableRow = styled(TableRow)`
  border-bottom: 1px solid #e3e5e8;
  &:last-child {
    border-bottom: none;
  }
`;

const ValueCell = styled(TableCell)`
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  height: 46px !important;
  color: #4b4f58;
  border-right: 1px solid #e3e5e8;
  border-bottom: 1px solid #e3e5e8;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0;
`;

const ChangeCell = styled(TableCell)<{ $last?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0;
`;

export const RealTable = ({
  periodText,
  monthNames,
  tableDetailedData,
}: {
  periodText: string;
  monthNames: string[];
  tableDetailedData: {
    category: string;
    data: {
      value: number;
      changeRate: ChangeRate;
    }[];
  }[];
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollToEnd = () => {
      // Table 컴포넌트 내부의 TableContainer를 찾아서 스크롤
      const tableContainer = tableContainerRef.current?.querySelector(
        '[data-slot="table-container"]'
      ) as HTMLDivElement;
      if (tableContainer) {
        tableContainer.scrollLeft = tableContainer.scrollWidth;
      }
    };

    const timeoutId = setTimeout(scrollToEnd, 100);
    return () => clearTimeout(timeoutId);
  }, [monthNames]);

  return (
    <StyledCard>
      <StyledCardHeader>
        <StyledCardTitle>{periodText}</StyledCardTitle>
      </StyledCardHeader>
      <StyledCardContent>
        <div ref={tableContainerRef}>
          <StyledTable>
            <TableHeader>
              <StyledTableRow>
                <StyledTableHead>항목</StyledTableHead>
                {monthNames.map((month, i) => (
                  <MonthTableHead
                    key={month}
                    colSpan={1}
                    $last={i === monthNames.length - 1}
                  >
                    {month}
                  </MonthTableHead>
                ))}
              </StyledTableRow>
            </TableHeader>
            <TableBody>
              {tableDetailedData.map((row, rowIdx) => (
                <DataTableRow key={rowIdx}>
                  <DataTableCell>
                    {row.category === "전체" ? "총 유효회원수" : row.category}
                  </DataTableCell>
                  {row.data.map((cellData, i) => (
                    <ValueCell key={`${row.category}-${i}`}>
                      {cellData.value.toFixed(0).toLocaleString()}명
                      <ChangeCell $last={i === row.data.length - 1}>
                        <MembersChangeIndicator
                          smallFont
                          showIcon={false}
                          changeRate={cellData.changeRate}
                        />
                      </ChangeCell>
                    </ValueCell>
                  ))}
                </DataTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </div>
      </StyledCardContent>
    </StyledCard>
  );
};
